/**
 * The theme engine. A person picks a few colours (the sidebar and top bar, and
 * optionally the accent, the selected item and the badges); everything else the
 * app paints with is worked out from them here, for the light theme and the
 * dark one, so any choice still reads well:
 *
 * - The sidebar's text is white or near-black, whichever reads better on it, with
 *   its quieter steps held to 4.5:1 (labels) and 3:1 (hints).
 * - The accent is deepened in the light theme, or lifted in the dark one, until
 *   it reads as text on the page at 4.5:1, and the text on it is chosen the same
 *   way. Its tint, its hover shade and its deep shade follow from it.
 * - The page's greys carry a trace of the sidebar's hue (none, subtle or rich).
 *
 * The maths is OKLCH (perceptual lightness, chroma, hue), so a step of lightness
 * looks like the same step whatever the hue; colours outside sRGB are brought
 * back in by lowering chroma, never by shifting hue. Semantic colours (late,
 * warn, done) are not themed: they carry meaning, so they stay in styles.css.
 */

export type Tint = 'none' | 'subtle' | 'rich';

export interface ThemeSpec {
  /** The sidebar and the top bar. */
  chrome: string;
  /** Buttons, links, focus and selection. Null: drawn from the sidebar colour. */
  accent: string | null;
  /** The selected page in the sidebar. Null: a lighter step of the sidebar. */
  active: string | null;
  /** Unread counts on the sidebar and the top bar. Null: the standard red. */
  badge: string | null;
  /** How much of the sidebar's hue the page's greys carry. */
  tint: Tint;
}

export interface Preset {
  id: string;
  label: string;
  spec: ThemeSpec;
}

const preset = (
  id: string,
  label: string,
  chrome: string,
  accent: string | null,
  extra: Partial<ThemeSpec> = {},
): Preset => ({
  id,
  label,
  spec: { chrome, accent, active: null, badge: null, tint: 'subtle', ...extra },
});

/** The gallery in Settings. The first is the app's own look. */
export const PRESETS: Preset[] = [
  preset('forest', '11Deeds', '#0d4b43', '#0f6e63'),
  preset('aubergine', 'Aubergine', '#4a154b', '#1264a3'),
  preset('midnight', 'Midnight', '#1a1d21', '#0b6fb8', { tint: 'none' }),
  preset('ocean', 'Ocean', '#0c3866', '#1f5fbf'),
  preset('indigo', 'Mood Indigo', '#22266b', '#4f5bd5'),
  preset('plum', 'Plum', '#3d1c5b', '#6b3fa0'),
  preset('rose', 'Rose', '#6a1636', '#a8325e'),
  preset('lagoon', 'Lagoon', '#0e5470', '#127a9e'),
  preset('jade', 'Jade', '#0b5c47', '#0f7b5f'),
  preset('clementine', 'Clementine', '#a63a12', '#b54708'),
  preset('espresso', 'Espresso', '#3a2a22', '#9a5b2e'),
  preset('graphite', 'Graphite', '#2c3038', '#3d6fd9', { tint: 'none' }),
  preset('sand', 'Sand', '#ebe4d6', '#8a5a24'),
  preset('cloud', 'Cloud', '#eceff4', '#2d5bd3', { tint: 'none' }),
];

export const DEFAULT_SPEC: ThemeSpec = PRESETS[0]!.spec;

/** The preset a spec is, if it is one exactly. */
export function presetOf(spec: ThemeSpec): Preset | undefined {
  return PRESETS.find((p) => sameSpec(p.spec, spec));
}

export function sameSpec(a: ThemeSpec, b: ThemeSpec): boolean {
  const eq = (x: string | null, y: string | null) =>
    (x ?? '').toLowerCase() === (y ?? '').toLowerCase();
  return (
    eq(a.chrome, b.chrome) &&
    eq(a.accent, b.accent) &&
    eq(a.active, b.active) &&
    eq(a.badge, b.badge) &&
    a.tint === b.tint
  );
}

// ---- colour maths

type Rgb = [number, number, number];
interface Lch {
  l: number;
  c: number;
  h: number;
}

const HEX = /^#?([0-9a-f]{3}|[0-9a-f]{6})$/i;

/** `#abc`, `abc`, `#aabbcc` to `#aabbcc`; null for anything else. */
export function normalizeHex(input: string): string | null {
  const m = HEX.exec(input.trim());
  if (!m) return null;
  const h = m[1]!.toLowerCase();
  return `#${h.length === 3 ? [...h].map((d) => d + d).join('') : h}`;
}

function hexToRgb(hex: string): Rgb {
  const h = normalizeHex(hex) ?? '#000000';
  return [1, 3, 5].map((i) => parseInt(h.slice(i, i + 2), 16) / 255) as Rgb;
}

function rgbToHex(rgb: Rgb): string {
  return `#${rgb
    .map((v) =>
      Math.round(Math.min(1, Math.max(0, v)) * 255)
        .toString(16)
        .padStart(2, '0'),
    )
    .join('')}`;
}

const toLinear = (v: number) => (v <= 0.04045 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4);
const toGamma = (v: number) => (v <= 0.0031308 ? 12.92 * v : 1.055 * v ** (1 / 2.4) - 0.055);

function rgbToOklab([r, g, b]: Rgb): Rgb {
  const [lr, lg, lb] = [toLinear(r), toLinear(g), toLinear(b)];
  const l = Math.cbrt(0.4122214708 * lr + 0.5363325363 * lg + 0.0514459929 * lb);
  const m = Math.cbrt(0.2119034982 * lr + 0.6806995451 * lg + 0.1073969566 * lb);
  const s = Math.cbrt(0.0883024619 * lr + 0.2817188376 * lg + 0.6299787005 * lb);
  return [
    0.2104542553 * l + 0.793617785 * m - 0.0040720468 * s,
    1.9779984951 * l - 2.428592205 * m + 0.4505937099 * s,
    0.0259040371 * l + 0.7827717662 * m - 0.808675766 * s,
  ];
}

/** OKLab to gamma sRGB, unclamped (a component outside 0…1 is out of gamut). */
function oklabToRgb([L, a, b]: Rgb): Rgb {
  const l = (L + 0.3963377774 * a + 0.2158037573 * b) ** 3;
  const m = (L - 0.1055613458 * a - 0.0638541728 * b) ** 3;
  const s = (L - 0.0894841775 * a - 1.291485548 * b) ** 3;
  return [
    toGamma(4.0767416621 * l - 3.3077115913 * m + 0.2309699292 * s),
    toGamma(-1.2684380046 * l + 2.6097574011 * m - 0.3413193965 * s),
    toGamma(-0.0041960863 * l - 0.7034186147 * m + 1.707614701 * s),
  ];
}

function toLch(hex: string): Lch {
  const [l, a, b] = rgbToOklab(hexToRgb(hex));
  const c = Math.hypot(a, b);
  const h = c < 1e-4 ? 0 : ((Math.atan2(b, a) * 180) / Math.PI + 360) % 360;
  return { l, c, h };
}

const inGamut = (rgb: Rgb) => rgb.every((v) => v >= -1e-4 && v <= 1 + 1e-4);

/** OKLCH to hex, lowering chroma (never moving hue or lightness) until it fits in sRGB. */
function fromLch({ l, c, h }: Lch): string {
  const L = Math.min(1, Math.max(0, l));
  const rad = (h * Math.PI) / 180;
  const at = (chroma: number): Rgb =>
    oklabToRgb([L, chroma * Math.cos(rad), chroma * Math.sin(rad)]);
  let rgb = at(c);
  if (!inGamut(rgb)) {
    let lo = 0;
    let hi = c;
    for (let i = 0; i < 24; i++) {
      const mid = (lo + hi) / 2;
      if (inGamut(at(mid))) lo = mid;
      else hi = mid;
    }
    rgb = at(lo);
  }
  return rgbToHex(rgb);
}

/** WCAG 2 relative luminance. */
function luminance(hex: string): number {
  const [r, g, b] = hexToRgb(hex).map(toLinear) as Rgb;
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

/** WCAG 2 contrast ratio, 1 to 21. */
export function contrast(a: string, b: string): number {
  const [x, y] = [luminance(a), luminance(b)].sort((p, q) => q - p) as [number, number];
  return (x + 0.05) / (y + 0.05);
}

/** `t` of the way from `a` to `b`, mixed in OKLab. */
export function mix(a: string, b: string, t: number): string {
  const [p, q] = [rgbToOklab(hexToRgb(a)), rgbToOklab(hexToRgb(b))];
  return rgbToHex(oklabToRgb(p.map((v, i) => v + (q[i]! - v) * t) as Rgb));
}

/**
 * The nearest lightness of `fg` that reaches `ratio` against `bg`, darker or
 * lighter, whichever needs the smaller step. When neither can, the end with the
 * most contrast.
 */
function reach(fg: Lch, bg: string, ratio: number): Lch {
  const at = (l: number) => contrast(fromLch({ ...fg, l }), bg);
  if (at(fg.l) >= ratio) return fg;
  for (let step = 0.01; step <= 1; step += 0.01) {
    const down = fg.l - step;
    const up = fg.l + step;
    if (down >= 0 && at(down) >= ratio) return { ...fg, l: down };
    if (up <= 1 && at(up) >= ratio) return { ...fg, l: up };
  }
  return { ...fg, l: at(0) >= at(1) ? 0 : 1 };
}

/** Mixes `bg` toward `ink` from `start` until the result reaches `ratio` on every one of `on`. */
function quietInk(bg: string, ink: string, on: string[], ratio: number, start: number): string {
  for (let t = start; t < 1; t += 0.02) {
    const c = mix(bg, ink, t);
    if (on.every((s) => contrast(c, s) >= ratio)) return c;
  }
  return ink;
}

const WHITE = '#ffffff';

/** White or a near-black of `hue`, whichever reads better on `bg`; white wins at 4.5:1. */
function inkFor(bg: string, hue: Lch): string {
  const dark = fromLch({ l: 0.17, c: Math.min(hue.c, 0.04), h: hue.h });
  const onWhite = contrast(WHITE, bg);
  if (onWhite >= 4.5) return WHITE;
  return contrast(dark, bg) > onWhite ? dark : WHITE;
}

// ---- the palette

export type Mode = 'light' | 'dark';

/** The tokens the engine owns, each a `#rrggbb`. styles.css owns the rest. */
export type Palette = Record<(typeof TOKENS)[number], string>;
export const TOKENS = [
  'paper',
  'paper-2',
  'ink',
  'ink-2',
  'ink-3',
  'line',
  'accent',
  'accent-ink',
  'accent-soft',
  'accent-deep',
  'on-accent',
  'chrome',
  'chrome-deep',
  'chrome-ink',
  'chrome-ink-2',
  'chrome-ink-3',
  'chrome-active',
  'chrome-active-ink',
  'chrome-badge',
  'on-chrome-badge',
] as const;

/** What the engine had to change in the colours the person picked, so Settings can say so. */
export interface Adjustments {
  /** The accent was deepened to read as text on the light page (the dark theme always lifts it). */
  accent: boolean;
  /** The sidebar was darkened for the dark theme. */
  chrome: boolean;
}

const TINT: Record<Tint, number> = { none: 0, subtle: 1, rich: 2.4 };
const BADGE = '#e0245e';
/** A default blue for an accent drawn from a sidebar with no hue of its own. */
const FALLBACK_ACCENT: Lch = { l: 0.5, c: 0.15, h: 255 };

/** The palette for one theme, and what had to change in the picked colours to get it. */
export function derive(spec: ThemeSpec, mode: Mode): { palette: Palette; adjusted: Adjustments } {
  const dark = mode === 'dark';
  const seed = toLch(normalizeHex(spec.chrome) ?? DEFAULT_SPEC.chrome);
  const accentSeed: Lch = spec.accent
    ? toLch(normalizeHex(spec.accent) ?? DEFAULT_SPEC.accent!)
    : seed.c < 0.03
      ? FALLBACK_ACCENT
      : { l: 0.5, c: Math.min(0.17, Math.max(0.09, seed.c * 1.1)), h: seed.h };

  // The page's greys: a trace of the sidebar's hue (or the accent's, if the sidebar is grey).
  const hueOf = seed.c >= 0.02 ? seed : accentSeed;
  const k = TINT[spec.tint];
  const cap = Math.max(hueOf.c, 0.02) * 0.6;
  const grey = (l: number, c: number) => fromLch({ l, c: Math.min(c * k, cap), h: hueOf.h });
  const paper = dark ? grey(0.205, 0.008) : grey(0.982, 0.0045);
  const paper2 = dark ? grey(0.238, 0.01) : grey(0.956, 0.007);
  const line = dark ? grey(0.32, 0.012) : grey(0.894, 0.009);
  const ink = dark ? grey(0.935, 0.005) : grey(0.235, 0.012);
  const ink2 = dark ? grey(0.8, 0.01) : grey(0.445, 0.014);
  const ink3 = fromLch(reach(toLch(dark ? grey(0.635, 0.012) : grey(0.585, 0.012)), paper2, 3));

  // The accent: reads as text on the page at 4.5:1.
  const base: Lch = dark
    ? { ...accentSeed, l: Math.max(accentSeed.l, 0.72), c: Math.min(accentSeed.c, 0.15) }
    : accentSeed;
  const fitted = reach(base, paper, 4.5);
  const accent = fromLch(fitted);
  const onAccent = dark
    ? fromLch(reach({ l: 0.2, c: Math.min(fitted.c, 0.04), h: fitted.h }, accent, 4.5))
    : inkFor(accent, fitted);
  const accentSoft = mix(paper, accent, dark ? 0.17 : 0.12);
  const accentInk = fromLch(
    reach(
      dark
        ? { l: Math.min(0.93, fitted.l + 0.08), c: fitted.c * 0.8, h: fitted.h }
        : { l: Math.max(0.2, fitted.l - 0.09), c: fitted.c, h: fitted.h },
      accentSoft,
      4.5,
    ),
  );
  const accentDeep = dark
    ? fromLch({ l: 0.27, c: Math.min(fitted.c, 0.07), h: fitted.h })
    : fromLch({
        l: Math.min(0.36, Math.max(0.26, fitted.l - 0.17)),
        c: Math.min(fitted.c, 0.1),
        h: fitted.h,
      });

  // The sidebar and the top bar. The dark theme holds it to a deep tone of the same hue.
  const chromeLch = dark
    ? { l: Math.min(0.3, Math.max(0.235, seed.l)), c: Math.min(seed.c, 0.09), h: seed.h }
    : seed;
  const chrome = fromLch(chromeLch);
  const chromeInk = inkFor(chrome, chromeLch);
  const lightText = chromeInk === WHITE;
  // A slightly different step toward the bottom, for the window's gradient: away from
  // the text (deeper under white, paler under dark), so it never costs contrast.
  const chromeDeep = fromLch({
    ...chromeLch,
    l: Math.min(1, Math.max(0, chromeLch.l + (lightText ? -0.045 : 0.025))),
  });
  const surfaces = [chrome, chromeDeep];
  const chromeInk2 = quietInk(chrome, chromeInk, surfaces, 4.5, 0.72);
  const chromeInk3 = quietInk(chrome, chromeInk, surfaces, 3, 0.55);

  const active = spec.active ? normalizeHex(spec.active) : null;
  const chromeActive = active
    ? dark
      ? fromLch({ ...toLch(active), l: Math.min(toLch(active).l, 0.5) })
      : active
    : autoActive(chrome, chromeInk, lightText);
  const chromeActiveInk =
    contrast(chromeInk, chromeActive) >= 4.5
      ? chromeInk
      : inkFor(chromeActive, toLch(chromeActive));

  const badge = normalizeHex(spec.badge ?? '') ?? BADGE;
  const onBadge = inkFor(badge, toLch(badge));

  return {
    palette: {
      paper,
      'paper-2': paper2,
      ink,
      'ink-2': ink2,
      'ink-3': ink3,
      line,
      accent,
      'accent-ink': accentInk,
      'accent-soft': accentSoft,
      'accent-deep': accentDeep,
      'on-accent': onAccent,
      chrome,
      'chrome-deep': chromeDeep,
      'chrome-ink': chromeInk,
      'chrome-ink-2': chromeInk2,
      'chrome-ink-3': chromeInk3,
      'chrome-active': chromeActive,
      'chrome-active-ink': chromeActiveInk,
      'chrome-badge': badge,
      'on-chrome-badge': onBadge,
    },
    adjusted: {
      accent: !dark && Boolean(spec.accent) && Math.abs(fitted.l - accentSeed.l) > 0.03,
      chrome: dark && Math.abs(chromeLch.l - seed.l) > 0.03,
    },
  };
}

/**
 * The selected page on the sidebar when no colour is picked: a lighter step of
 * the sidebar under white text (darker under dark text), as strong as the text
 * on it allows; on a sidebar too close to that limit, a step the other way.
 */
function autoActive(chrome: string, ink: string, lightText: boolean): string {
  for (const t of lightText ? [0.2, 0.16, 0.12, 0.09] : [0.14, 0.11, 0.08]) {
    const step = mix(chrome, ink, t);
    if (contrast(ink, step) >= 4.5) return step;
  }
  return mix(chrome, lightText ? '#000000' : WHITE, 0.2);
}

const block = (selector: string, p: Palette) =>
  `${selector}{${TOKENS.map((t) => `--${t}:${p[t]};`).join('')}}`;

/**
 * The stylesheet for a spec: the light palette, and the dark one both for an
 * explicit dark theme and for "match the system". Its selectors outrank the
 * defaults in styles.css whatever order the two load in.
 */
export function paletteCss(spec: ThemeSpec): string {
  const light = derive(spec, 'light').palette;
  const dark = derive(spec, 'dark').palette;
  return [
    block('html[data-palette]:root', light),
    `@media (prefers-color-scheme: dark){${block("html[data-palette]:root:not([data-theme='light'])", dark)}}`,
    block("html[data-palette][data-theme='dark']:root", dark),
  ].join('');
}

// ---- sharing a theme, as Slack does: one line of colours

/** `#0d4b43,#0f6e63,auto,auto,subtle`: sidebar, accent, selected, badges, tint. */
export function encodeSpec(spec: ThemeSpec): string {
  return [
    spec.chrome,
    spec.accent ?? 'auto',
    spec.active ?? 'auto',
    spec.badge ?? 'auto',
    spec.tint,
  ]
    .map((v) => v.toLowerCase())
    .join(',');
}

/** The reverse of `encodeSpec`, forgiving about spaces and `#`; null when the sidebar colour is missing. */
export function decodeSpec(text: string): ThemeSpec | null {
  const parts = text
    .split(/[,;\s]+/)
    .map((p) => p.trim())
    .filter(Boolean);
  const chrome = normalizeHex(parts[0] ?? '');
  if (!chrome) return null;
  const colour = (v: string | undefined) => (v ? normalizeHex(v) : null);
  const tint = parts.find((p): p is Tint => p === 'none' || p === 'subtle' || p === 'rich');
  return {
    chrome,
    accent: colour(parts[1]),
    active: colour(parts[2]),
    badge: colour(parts[3]),
    tint: tint ?? 'subtle',
  };
}

/** A spec as stored, or null when it is not one. */
export function parseSpec(value: unknown): ThemeSpec | null {
  if (!value || typeof value !== 'object') return null;
  const v = value as Record<string, unknown>;
  const chrome = typeof v['chrome'] === 'string' ? normalizeHex(v['chrome']) : null;
  if (!chrome) return null;
  const colour = (x: unknown) => (typeof x === 'string' ? normalizeHex(x) : null);
  const tint = v['tint'] === 'none' || v['tint'] === 'rich' ? v['tint'] : 'subtle';
  return {
    chrome,
    accent: colour(v['accent']),
    active: colour(v['active']),
    badge: colour(v['badge']),
    tint,
  };
}
